/**
 * Tracked-edit review of a Word document, inside the workspace: the existing
 * review editor (IManageDocumentReview), with the workspace's folders as the
 * destinations for "save as new document".
 *
 * @module components/apps/imanage/IManageReviewPanel
 */
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import { ArrowLeftIcon, ChevronRightIcon, FolderIcon } from "lucide-react";
import { useState } from "react";

import { IManageDocumentReview } from "../integrations/IManageDocumentReview";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Skeleton } from "../../ui/skeleton";
import { useChildren } from "./imanageQueries";
import { InlineError } from "./IManageParts";

export function IManageReviewPanel({
  organizationId,
  library,
  workspaceId,
  document,
  folder: initialFolder,
  canUpload,
  canSaveVersion,
  onClose,
  onSaved,
}: {
  readonly organizationId: string;
  readonly library: string;
  readonly workspaceId: string;
  readonly document: IManageDocument;
  readonly folder: IManageEntry | null;
  readonly canUpload: boolean;
  readonly canSaveVersion: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const [folder, setFolder] = useState<Pick<IManageEntry, "id" | "name"> | null>(initialFolder);
  const [picking, setPicking] = useState(false);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <IManageDocumentReview
        key={document.id}
        organizationId={organizationId}
        libraryId={library}
        document={document}
        folder={folder}
        canUpload={canUpload}
        canSaveVersion={canSaveVersion}
        onPickFolder={() => setPicking(true)}
        onClose={onClose}
        onSaved={onSaved}
      />
      {picking ? (
        <FolderPicker
          organizationId={organizationId}
          library={library}
          workspaceId={workspaceId}
          onPick={(picked) => {
            setFolder(picked);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>
  );
}

/** Choose a destination folder in the workspace, walking into sub-folders. */
function FolderPicker({
  organizationId,
  library,
  workspaceId,
  onPick,
  onClose,
}: {
  readonly organizationId: string;
  readonly library: string;
  readonly workspaceId: string;
  readonly onPick: (folder: IManageEntry) => void;
  readonly onClose: () => void;
}) {
  const [trail, setTrail] = useState<IManageEntry[]>([]);
  const current = trail.at(-1) ?? null;
  const children = useChildren(
    organizationId,
    library,
    current ? { id: current.id, kind: "folder" } : { id: workspaceId, kind: "matter" },
  );
  const folders = (children.data?.entries ?? []).filter((entry) => entry.kind === "folder");
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Choose a folder</DialogTitle>
          <DialogDescription>The reviewed document is saved as a new document here.</DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-2">
          {current ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
              onClick={() => setTrail((t) => t.slice(0, -1))}
            >
              <ArrowLeftIcon aria-hidden className="size-3.5" />
              {trail.length > 1 ? trail.at(-2)!.name : "Workspace"}
            </button>
          ) : null}
          {children.error && !children.data ? (
            <InlineError message={children.error} onRetry={children.refresh} />
          ) : children.loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : folders.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-muted-foreground">No sub-folders.</p>
          ) : (
            <ul className="flex flex-col rounded-lg border border-border/70">
              {folders.map((f) => (
                <li key={f.id} className="flex items-center border-b border-border/50 last:border-0">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left text-[13px] hover:bg-accent/60"
                    onClick={() => onPick(f)}
                  >
                    <FolderIcon aria-hidden className="size-4 text-muted-foreground" />
                    <span className="truncate">{f.name}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Open ${f.name}`}
                    className="flex h-9 w-9 items-center justify-center text-muted-foreground hover:text-foreground"
                    onClick={() => setTrail((t) => [...t, f])}
                  >
                    <ChevronRightIcon aria-hidden className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogPanel>
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {current ? (
            <Button size="sm" onClick={() => onPick(current)}>
              Use {current.name}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
